import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ConfigOptions, ConfigService } from '@wilson/config';
import {
  CreateProductBookingPayload,
  GetProductBookingsParams,
  ProductBooking,
  UpdateProductBookingPayload,
} from '@wilson/interfaces';
import { ProductBookingModel } from '@wilson/product-bookings/models';
import { toCalendarDate } from '@wilson/utils';
import { map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ProductBookingsGateway {
  private readonly httpClient = inject(HttpClient);
  private readonly config = inject<ConfigOptions>(ConfigService);

  protected readonly path = 'product-bookings';

  public getProductBookings(
    params: GetProductBookingsParams,
  ): Observable<ProductBookingModel[]> {
    let queryParams = new HttpParams()
      .set('from', toCalendarDate(params.from))
      .set('to', toCalendarDate(params.to));

    if (params.agreementIds?.length) {
      queryParams = queryParams.set(
        'agreementIds',
        params.agreementIds.join(','),
      );
    }

    if (params.productId) {
      queryParams = queryParams.set('productId', params.productId);
    }

    if (params.shiftId) {
      queryParams = queryParams.set('shiftId', params.shiftId);
    }

    return this.httpClient
      .get<ProductBooking[]>(`${this.config.host}/${this.path}`, {
        params: queryParams,
      })
      .pipe(
        map((bookings) =>
          (bookings ?? []).map(ProductBookingModel.fromApiResponse),
        ),
      );
  }

  public createProductBooking(
    payload: CreateProductBookingPayload,
  ): Observable<Omit<ProductBooking, 'product'>> {
    return this.httpClient.post<Omit<ProductBooking, 'product'>>(
      `${this.config.host}/${this.path}`,
      { ...payload, date: toCalendarDate(payload.date) },
    );
  }

  public updateProductBooking(
    id: string,
    payload: UpdateProductBookingPayload,
  ): Observable<Omit<ProductBooking, 'product'>> {
    return this.httpClient.patch<Omit<ProductBooking, 'product'>>(
      `${this.config.host}/${this.path}/${id}`,
      payload,
    );
  }

  public deleteProductBooking(
    id: string,
  ): Observable<Omit<ProductBooking, 'product'>> {
    return this.httpClient.delete<Omit<ProductBooking, 'product'>>(
      `${this.config.host}/${this.path}/${id}`,
    );
  }
}
